import 'twin.macro';
import ApiDropdown from 'components/dropdown/api-dropdown/ApiDropdown';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Input from 'components/form/input/Input';
import Button from 'components/button/Button';
import TextEditor from 'components/form/text-editor/TextEditor';
import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';

import { EditorState } from 'draft-js';
import {
  createStateFromDefaultValue,
  suggestions,
} from 'utils/templateHelpers';
import { Template } from 'types/templateTypes';
import { useParams, useLocation, Link } from 'react-router-dom';
import AutoSuggest from 'components/auto-suggest/AutoSuggest';
import { Body, Footnote } from 'components/typography/Typography';
import { Routes } from 'types/routeTypes';
import Label from 'components/form/Label';
import { useQuery } from 'hooks/sympl-query';
import { GET_TEMPLATE } from 'graphql/templates/queries';
import { CaretLeft, EnvelopeSimple } from '@phosphor-icons/react';
import useNavigationContext from 'hooks/context/nav-context';
import { useNavigate } from 'react-router-dom';
import { getLanguageValue } from 'utils/languageHelpers';
import { fireEvent } from 'utils/eventHelper';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import useBeforeUnloadWarning from 'hooks/useBeforeUnloadWarning';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useKeptVersion } from 'api/keptVersion';
import {
  TEMPLATES_PATH,
  TemplateWork,
  missingForTemplate,
  saveTemplate,
  templateBody,
} from 'api/templateAutoSave';

interface TemplateState {
  language?: number;
  subject?: string;
  body?: string;
}

const TemplateEditor: React.FC = () => {
  const location = useLocation();
  const defaultTemplate = location?.state as TemplateState;
  const navigate = useNavigate();
  const { template: templateId } = useParams<{ template?: string }>();
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createEmpty()
  );
  const { isAdmin } = useNavigationContext();
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();

  const { loading, data } = useQuery<{ template: Template }>(GET_TEMPLATE, {
    // Never the remembered copy: this screen stores what it shows, and an
    // older copy shown first would be stored over the newer template.
    fetchPolicy: 'network-only',
    variables: { id: templateId },
    skip: !templateId,
  });

  const originalTemplate = useMemo(
    () =>
      data?.template ??
      ({
        name: '',
        body: defaultTemplate?.body ?? '',
        language_id: defaultTemplate?.language ?? 141,
        subject: defaultTemplate?.subject ?? '',
      } as Template),
    [data?.template, defaultTemplate]
  );

  const readOnly = useMemo(
    () =>
      data?.template && // TEMPLATE AVAILABLE
      (!!data?.template.slug || !data?.template.customer_id) && // HAS SLUG OR IS NOT FROM CUSTOMER
      !isAdmin, // NOT ADMIN
    [data?.template, isAdmin]
  );

  /*
   * Auto-save (scenes 16 and 21). The screen's work is four things: name,
   * language, subject and text. `loadedFor` says for which template they
   * were taken over from the server ("new" for one that does not exist yet).
   */
  const [work, setWork] = useState<TemplateWork>();
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  /** Raised to draw the boxes again from `work` (after Undo or Redo). */
  const [drawn, setDrawn] = useState(0);
  const drawnNow = useRef(drawn);
  drawnNow.current = drawn;
  const here = templateId ?? 'new';

  const takenFor = useRef<string | null>(null);
  useEffect(() => {
    if (templateId && !data?.template) return;
    // Once per template: were the loaded template handed over a second
    // time (it is shared with other parts of the app), taking it over
    // again would put the boxes back under the person's hands. A guard;
    // not something that was seen happening.
    if (takenFor.current === here) return;
    takenFor.current = here;
    const taken: TemplateWork = {
      name: originalTemplate.name ?? '',
      language_id: Number(originalTemplate.language_id ?? 141),
      subject: originalTemplate.subject ?? '',
      body: originalTemplate.body ?? '',
    };
    setWork(taken);
    setEditorState(createStateFromDefaultValue(taken.body));
    setLoadedFor(here);
    setDrawn((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalTemplate, here]);

  const own = loadedFor === here && !!work;
  /** A template that exists and may be changed stores itself. */
  const storesItself = own && !!templateId && !readOnly;
  const languageName = getLanguageValue(work?.language_id ?? 141) || 'English';

  const missing = work ? missingForTemplate(work) : [];
  const incomplete = missing.length > 0;
  /*
   * What the server holds of this template, as far as the screen knows.
   * While a required field is empty the saver is handed THIS, not the
   * screen: nothing goes out, and a store that was already waiting for its
   * pause is called off (D34, D35: all or nothing on a settings screen).
   */
  const storedWork = useRef<{ here: string; work: TemplateWork }>();
  if (own && work && storedWork.current?.here !== here) {
    storedWork.current = { here, work };
  }
  if (!own) storedWork.current = undefined;
  const print = work ? JSON.stringify(work) : null;
  const storedPrint = storedWork.current
    ? JSON.stringify(storedWork.current.work)
    : null;
  const storable = useMemo<TemplateWork | undefined>(
    () => {
      if (!storesItself || !print) return undefined;
      return JSON.parse(
        incomplete && storedPrint ? storedPrint : print
      ) as TemplateWork;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storesItself, print, incomplete, storedPrint]
  );
  const heldBack = storesItself && incomplete && print !== storedPrint;
  const version = useKeptVersion('template', templateId ?? null);
  const heldBackRef = useRef(heldBack);
  heldBackRef.current = heldBack;

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<TemplateWork>({
    value: storable,
    enabled: storesItself && !!storable,
    identity: templateId ? `template:${templateId}` : null,
    save: async (value, signal) => {
      const id = templateId as string;
      await saveTemplate(value, id, signal, version.basedOn(id), (count) =>
        version.stored(id, count)
      );
      if (storedWork.current?.here === id) {
        storedWork.current = { here: id, work: value };
      }
      fireEvent('update_mail_template', { template: Number(id) });
    },
    flushOnHide: (value) => {
      if (templateId)
        // With the count: refused when someone else changed the template.
        sendKeepaliveSave(
          TEMPLATES_PATH,
          templateBody(value, templateId),
          'POST',
          version.basedOn(templateId)
        );
    },
  });

  const { canUndo, canRedo, undo, redo } = useUndoRedo<TemplateWork>({
    value: own ? work : undefined,
    enabled: storesItself,
    identity: templateId ? `template:${templateId}` : null,
    same: (a, b) => JSON.stringify(a) === JSON.stringify(b),
    apply: (earlier) => {
      setWork(earlier);
      setEditorState(createStateFromDefaultValue(earlier.body));
      // The boxes only start from their values: drawn again.
      setDrawn((n) => n + 1);
    },
  });

  useBeforeUnloadWarning(heldBack);

  // What the sidebar asks before taking the person away.
  useEffect(() => {
    if (!storesItself) return undefined;
    setPending(
      (hasPending && hasFailed) || heldBack,
      heldBack ? missing[0] : undefined
    );
    registerAbandon(abandon);
    registerSaveNow(
      () => (heldBackRef.current ? Promise.resolve(false) : saveNow()),
      () => !heldBackRef.current && isKept()
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storesItself,
    hasPending,
    hasFailed,
    heldBack,
    missing[0],
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  /** One field of the work changes; only the boxes on screen speak. */
  const change = (field: keyof TemplateWork, value: string | number) => {
    if (drawnNow.current !== drawn) return;
    setWork((now) => {
      if (!now) return now;
      /*
       * The text box writes the same words with or without a line end
       * after them, and says so again when the caret leaves it. That is not
       * a change: taken for one, it became a step of its own at the very
       * press on Redo and wiped what Redo was to bring back (Firefox, night
       * run of 6 Oct 2026).
       */
      if (
        field === 'body' &&
        String(value).trimEnd() === String(now.body).trimEnd()
      )
        return now;
      return { ...now, [field]: value };
    });
  };

  /*
   * A template that does not exist yet: nothing is stored until "Create"
   * is pressed (scene 16). Then it exists, and keeps itself.
   */
  const [creating, setCreating] = useState(false);
  const [createProblem, setCreateProblem] = useState<string | null>(null);
  const create = () => {
    if (!work || creating) return;
    if (incomplete) {
      setCreateProblem(`Not created: ${missing[0]}`);
      return;
    }
    setCreating(true);
    setCreateProblem(null);
    saveTemplate(work)
      .then((id) => {
        fireEvent('update_mail_template', { template: id });
        navigate(`${Routes.TEMPLATE_SETTINGS}/${id}`, { replace: true });
      })
      .catch(() => setCreateProblem('Not created. Try again.'))
      .finally(() => setCreating(false));
  };

  const [visible, setVisible] = useState(false);
  const editorWrapperRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleWindowClick = (e: MouseEvent) => {
      // Hide the autosuggest if the click was outside of the editor
      // Don't use onBlur of text editor as it will hide the autosuggest when the user clicks on it
      if (!editorWrapperRef.current?.contains(e.target as Node))
        setVisible(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '#') setVisible(true);
      else setVisible(false);
    };

    document.addEventListener('click', handleWindowClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleWindowClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (loading || !own || !work) return <div>Loading</div>;

  return (
    <div tw="p-1">
      <div tw="flex justify-between">
        <Link
          tw="flex items-center justify-between text-gray-600"
          to={Routes.TEMPLATE_SETTINGS}
        >
          <div tw="flex flex-row items-center">
            <CaretLeft weight="bold" size={20} tw="mr-1" />
            <Body>Back to overview</Body>
          </div>
        </Link>
        <div tw="flex flex-row-reverse">
          {storesItself && (
            <div tw="flex items-center gap-2">
              <UndoRedo
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={undo}
                onRedo={redo}
              />
              <SaveStatus
                state={saveState}
                keptAt={keptAt}
                switching={switching}
                notSavedBecause={
                  heldBack ? `Not saved: ${missing[0]}` : undefined
                }
              />
            </div>
          )}
          {!templateId && (
            <div tw="flex items-center gap-3">
              {createProblem && (
                <p tw="text-sm text-red-600" data-testid="template-problem">
                  {createProblem}
                </p>
              )}
              <Button
                testId="template-create"
                disabled={creating}
                onClick={create}
              >
                Create
              </Button>
            </div>
          )}
        </div>
      </div>
      <OutOfDateBox
        refusal={refusal}
        onLoadTheirs={() => {
          abandon();
          window.setTimeout(() => window.location.reload(), 100);
        }}
        onKeepMine={() => {
          version.keepMine();
          retryRefused();
        }}
      />
      <form
        // Drawn again after Undo and Redo: the boxes start from their values.
        key={drawn}
        id="template-form"
        tw="flex flex-col my-4"
        onSubmit={(event) => event.preventDefault()}
      >
        <div tw="flex flex-row gap-4">
          <section tw="flex-1">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              defaultValue={work.name}
              disabled={readOnly}
              onChange={(e) => change('name', e.target.value)}
            />
          </section>
          <section>
            <Label htmlFor="language_id">Language</Label>
            <div>
              <ApiDropdown
                enableSearch
                mode="key"
                type="languages"
                value={work.language_id}
                disabled={readOnly}
                onChange={(item) => {
                  if (!item) return;
                  change(
                    'language_id',
                    Number(typeof item === 'string' ? item : item.key)
                  );
                }}
              />
            </div>
          </section>
        </div>
        <div tw="grow">
          <div>
            <Label htmlFor="subject">Subject</Label>
            <div>
              <Input
                id="subject"
                name="subject"
                defaultValue={work.subject}
                disabled={readOnly}
                onChange={(e) => change('subject', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="body">Body</Label>
            <div data-testid="template-body">
              <TextEditor
                identifier="template_editor"
                editorWrapperRef={editorWrapperRef}
                defaultValue={work.body}
                externalState={editorState}
                disabled={readOnly}
                setExternalState={setEditorState}
                autoSuggest={
                  <AutoSuggest
                    editorWrapperRef={editorWrapperRef}
                    visible={visible}
                    setVisible={setVisible}
                    suggestions={suggestions}
                    editorState={editorState}
                    setEditorState={setEditorState}
                  />
                }
                onChange={(value) => change('body', value)}
                aiEnabled={!readOnly}
                initialContext={[
                  {
                    role: 'assistant',
                    content: `You always reply in ${languageName}`,
                  },
                  {
                    role: 'assistant',
                    content: `Every line should be wrapped inside a <p></p> tag.`,
                  },
                ]}
                aiSuggestedActions={[
                  {
                    icon: <EnvelopeSimple weight="bold" tw="text-indigo-500" />,
                    label: 'Write a template about ...',
                    prompt: 'Write a template about ',
                  },
                ]}
              />
              <Footnote mt={1}>Tip: Type # to insert variables.</Footnote>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TemplateEditor;
